import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy, TriangleAlert } from 'lucide-react'
import type { IssuedKey } from '@/api/catalogApi'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

/**
 * Shown once, right after a key is issued or rotated: everything a product
 * needs to connect, each with a copy button. It closes only after the admin
 * confirms they copied it — the secret is never readable again.
 */
export function IssuedKeyDialog({ issued, baseUrl, onClose }: { issued: IssuedKey; baseUrl: string; onClose: () => void }) {
  const { t } = useTranslation()
  const [confirmed, setConfirmed] = useState(false)
  return (
    <Dialog open onOpenChange={(o) => !o && confirmed && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('catalog.apiAccess.issuedTitle')}</DialogTitle>
        </DialogHeader>
        <p className="flex gap-2 rounded-md bg-note-bg px-3 py-2 text-meta text-ink">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-sla-warning" aria-hidden />
          {t('catalog.apiAccess.issuedWarning')}
        </p>
        <dl className="flex flex-col gap-3">
          <CopyRow label={t('catalog.apiAccess.cgtValue')} value={issued.apiKeyValue} strong />
          <CopyRow label={t('catalog.apiAccess.baseUrl')} value={baseUrl} />
          <CopyRow label={t('catalog.apiAccess.clientId')} value={issued.client.clientId} />
          {issued.clientSecret ? (
            <CopyRow label={t('catalog.apiAccess.clientSecret')} value={issued.clientSecret} />
          ) : (
            <div className="flex flex-col gap-1">
              <dt className="text-meta font-medium text-ink-muted">{t('catalog.apiAccess.clientSecret')}</dt>
              <dd className="text-body text-ink-muted">{t('catalog.apiAccess.noSecret')}</dd>
            </div>
          )}
        </dl>
        <label className="flex items-center gap-2 text-body text-ink">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          {t('catalog.apiAccess.confirmCopied')}
        </label>
        <DialogFooter>
          <Button disabled={!confirmed} onClick={onClose}>
            {t('catalog.apiAccess.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CopyRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-meta font-medium text-ink-muted">{label}</dt>
      <dd className="flex items-center gap-2">
        <code className={`min-w-0 flex-1 break-all rounded-md border border-line bg-surface-subtle px-2 py-1.5 font-mono text-label ${strong ? 'text-ink-strong' : 'text-ink'}`}>
          {value}
        </code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true)
              window.setTimeout(() => setCopied(false), 1500)
            })
          }
        >
          {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
          {copied ? t('catalog.apiAccess.copied') : t('catalog.apiAccess.copy')}
        </Button>
      </dd>
    </div>
  )
}
